/** Durable PDF ink v1. Geometry is in PDF user space, never CSS pixels.
 * Null Bézier controls encode a move/straight segment; PDF.js uses NaN internally.
 * PDF.js editor IDs, comments, scripts and other annotation types are not persisted here.
 */
export interface PdfInkAnnotation {
  id:string; pageIndex:number; rect:[number,number,number,number]; rotation:0|90|180|270;
  color:[number,number,number]; thickness:number; opacity:number;
  paths:{lines:Array<Array<number|null>>;points:number[][]};
}
export interface PdfInkPage {pageIndex:number;revision:number;mutationId:string|null;annotations:PdfInkAnnotation[];updatedAt:string|null}
export interface PdfInkDocument {schemaVersion:1;fileId:string;contentRevision:string;pages:PdfInkPage[]}
export interface PdfInkPageUpdate {schemaVersion:1;contentRevision:string;baseRevision:number;mutationId:string;annotations:PdfInkAnnotation[]}
export const PDF_INK_MAX_PAGE_BYTES=1_048_576;
export const PDF_INK_MAX_PAGE_INDEX=99_999;
export const PDF_INK_UUID=/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
export const isPdfInkContentRevision=(value:unknown):value is string=>typeof value==='string'&&/^[a-f0-9]{64}$/.test(value);
function check(ok:unknown):asserts ok {if(!ok)throw new Error('手写批注数据无效');}
function record(value:unknown,keys:string[]):Record<string,unknown>{check(value!==null&&typeof value==='object'&&!Array.isArray(value));const row=value as Record<string,unknown>;check(Object.keys(row).length===keys.length&&keys.every(key=>Object.hasOwn(row,key)));return row;}
const coordinate=(value:unknown):value is number=>typeof value==='number'&&Number.isFinite(value)&&Math.abs(value)<=1_000_000;
export function parsePdfInkAnnotations(value:unknown,pageIndex:number):PdfInkAnnotation[]{
  check(Number.isSafeInteger(pageIndex)&&pageIndex>=0&&pageIndex<=PDF_INK_MAX_PAGE_INDEX);
  check(Array.isArray(value)&&value.length<=4096);const ids=new Set<string>();let coordinates=0;
  for(const item of value){
    const a=record(item,['id','pageIndex','rect','rotation','color','thickness','opacity','paths']);
    check(typeof a.id==='string'&&PDF_INK_UUID.test(a.id)&&!ids.has(a.id));ids.add(a.id);
    check(a.pageIndex===pageIndex&&[0,90,180,270].includes(a.rotation as number));
    check(Array.isArray(a.rect)&&a.rect.length===4&&a.rect.every(coordinate)&&a.rect[2]>=a.rect[0]&&a.rect[3]>=a.rect[1]);
    check(Array.isArray(a.color)&&a.color.length===3&&a.color.every(n=>Number.isInteger(n)&&n>=0&&n<=255));
    check(typeof a.thickness==='number'&&Number.isFinite(a.thickness)&&a.thickness>0&&a.thickness<=100);
    check(typeof a.opacity==='number'&&Number.isFinite(a.opacity)&&a.opacity>0&&a.opacity<=1);
    const p=record(a.paths,['lines','points']);check(Array.isArray(p.lines)&&Array.isArray(p.points)&&p.lines.length===p.points.length&&p.lines.length>0&&p.lines.length<=4096);
    for(let i=0;i<p.lines.length;i++){
      const line=p.lines[i],points=p.points[i];check(Array.isArray(line)&&line.length>=6&&line.length%6===0&&Array.isArray(points)&&points.length>=2&&points.length%2===0);
      coordinates+=line.length+points.length;check(coordinates<=150_000);
      check(points.every(coordinate));
      for(let j=0;j<line.length;j+=6){
        check(coordinate(line[j+4])&&coordinate(line[j+5]));
        const controls=line.slice(j,j+4);check(controls.every(coordinate)||controls.every(n=>n===null));
        if(j===0)check(controls.every(n=>n===null));
      }
    }
  }
  check(new TextEncoder().encode(JSON.stringify(value)).byteLength<=PDF_INK_MAX_PAGE_BYTES);
  return structuredClone(value) as PdfInkAnnotation[];
}
export function parsePdfInkPageUpdate(value:unknown,pageIndex:number):PdfInkPageUpdate {
  const row=record(value,['schemaVersion','contentRevision','baseRevision','mutationId','annotations']);
  check(row.schemaVersion===1&&isPdfInkContentRevision(row.contentRevision));
  check(Number.isSafeInteger(row.baseRevision)&&(row.baseRevision as number)>=0&&(row.baseRevision as number)<Number.MAX_SAFE_INTEGER);
  check(typeof row.mutationId==='string'&&PDF_INK_UUID.test(row.mutationId));
  return {...row,annotations:parsePdfInkAnnotations(row.annotations,pageIndex)} as unknown as PdfInkPageUpdate;
}
